// src/pages/SaveBookmarks.jsx - Redesigned with Modern Orange-to-Pink Theme
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchNavigation from '../components/SearchNavigation';
import ApiService from '../services/apiService';

const SaveBookmarks = () => {
  const navigate = useNavigate();

  // State management
  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [isLoadingCourts, setIsLoadingCourts] = useState(true);
  const [courtsError, setCourtsError] = useState('');

  const [judgements, setJudgements] = useState([]);
  const [isLoadingJudgements, setIsLoadingJudgements] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedYear, setSelectedYear] = useState('2025');

  useEffect(() => {
    loadCourts();
  }, []);

  // Load courts from API
  const loadCourts = async () => {
    try {
      setIsLoadingCourts(true);
      setCourtsError('');
      console.log('🏛️ Loading bookmarks from API...');

      const courtsData = await ApiService.getBookmarksList();

      console.log('✅ Bookmarks loaded:', courtsData);
      setCourts(courtsData);
      
      // Auto-select first bookmark
      if (courtsData.length > 0) {
        setSelectedCourt(courtsData[0]);
        loadJudgements(courtsData[0], currentPage, selectedYear);
      }

    } catch (error) {
      console.error('❌ Error loading bookmarks:', error);
      setCourtsError(error.message || 'Failed to load bookmarks');
    } finally {
      setIsLoadingCourts(false);
    }
  };

  const formatDate = (dateStr) => {
    if (dateStr) {
      try {
        if (dateStr.length === 8) {
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          return `${day}/${month}/${year}`;
        }
        return dateStr;
      } catch {
        return dateStr;
      }
    }
    return 'Date not available';
  };

  // Load judgements for selected bookmark
  const loadJudgements = async (courtKey, page, year) => {
    try {
      setIsLoadingJudgements(true);
      console.log(`📊 Loading judgements for ${courtKey}, Page: ${page}, Year: ${year}`);
      const apiResponse = await ApiService.getBookmarkItems(courtKey);
      setJudgements(apiResponse);

      const resultsData = {
        results: [],
        totalCount: 0,
        query: ``.trim(),
        searchType: 'Bookmark Search',
        timestamp: new Date().toISOString(),
        courtsList: [],
        yearList: [],
        searchData: {
          yearFrom: 2025,
          yearTo: 2025,
          mainkeys: 'All'
        }
      };

      localStorage.setItem('searchResults', JSON.stringify(resultsData));

    } catch (error) {
      console.error('❌ Error loading judgements:', error);
    } finally {
      setIsLoadingJudgements(false);
    }
  };

  const handleCourtSelect = (courtKey) => {
    setSelectedCourt(courtKey);
    setCurrentPage(1);
    loadJudgements(courtKey, 1, selectedYear);
  };

  const handleDelete = async (id) => {
    try {
      await ApiService.deleteBookmarkItem(id);
      loadJudgements(selectedCourt, 1, selectedYear);
    } catch (error) {
      console.error('Error deleting bookmark:', error);
    }
  };

  const handleJudgementClick = (judgement) => {
    console.log('Judgement clicked:', judgement);
    const currentIndex = judgements.findIndex(result => result.highlightLink === judgement.highlightLink);
    const navigationData = {
      currentIndex: currentIndex,
      results: judgements.map(result => ({
        keycode: result.highlightLink,
        title: '',
        court: '',
        date: ''
      })),
      searchQuery: '',
      totalResults: judgements.length,
      currentPage: 1
    };

    sessionStorage.setItem('judgementNavigation', JSON.stringify(navigationData));
    navigate(`/judgement/${judgement.highlightLink}`);
  };

  return (
    <div className="bookmarks-layout">
      <Navbar />
      <SearchNavigation />

      <div className="bookmarks-main-content">
        {/* Hero Section */}
        <div className="bookmarks-hero">
          <div className="hero-badge">
            <i className="bx bx-bookmark-heart"></i>
            <span>Saved Bookmarks</span>
          </div>
          <h1 className="hero-title">
            Your Saved Cases
          </h1>
          <p className="hero-subtitle">
            Quick access to all your bookmarked legal cases
          </p>
        </div>

        <div className="bookmarks-container">
          <div className="bookmarks-grid">
            {/* Left Sidebar - Bookmarks List */}
            <div className="bookmarks-sidebar">
              <div className="sidebar-header">
                <div className="header-icon">
                  <i className="bx bx-bookmarks"></i>
                </div>
                <div className="header-content">
                  <h3 className="sidebar-title">Bookmark Collections</h3>
                  <p className="sidebar-subtitle">{courts.length} collection{courts.length !== 1 ? 's' : ''}</p>
                </div>
              </div>

              <div className="sidebar-body">
                {isLoadingCourts ? (
                  <div className="loading-state">
                    <i className="bx bx-loader-alt bx-spin"></i>
                    <p>Loading bookmarks...</p>
                  </div>
                ) : courtsError ? (
                  <div className="error-state">
                    <i className="bx bx-error-circle"></i>
                    <p>{courtsError}</p>
                  </div>
                ) : courts.length === 0 ? (
                  <div className="empty-state">
                    <i className="bx bx-bookmark-minus"></i>
                    <p>No bookmarks found</p>
                  </div>
                ) : (
                  <div className="bookmarks-list">
                    {courts.map((item, index) => (
                      <div
                        key={item}
                        className={`bookmark-item ${selectedCourt === item ? 'active' : ''}`}
                        onClick={() => handleCourtSelect(item)}
                      >
                        <div className="bookmark-icon">
                          <i className="bx bx-folder"></i>
                        </div>
                        <div className="bookmark-info">
                          <span className="bookmark-name">{item}</span>
                        </div>
                        <div className="bookmark-arrow">
                          <i className="bx bx-chevron-right"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Content - Cases Table */}
            <div className="cases-content">
              <div className="content-header">
                <div className="header-info">
                  <h3 className="content-title">
                    {selectedCourt ? selectedCourt : 'Select a bookmark collection'}
                  </h3>
                  <p className="content-subtitle">
                    {judgements.length} case{judgements.length !== 1 ? 's' : ''} saved
                  </p>
                </div>
              </div>

              <div className="content-body">
                {isLoadingJudgements ? (
                  <div className="loading-table">
                    <i className="bx bx-loader-alt bx-spin"></i>
                    <p>Loading cases...</p>
                  </div>
                ) : judgements.length === 0 ? (
                  <div className="empty-table">
                    <i className="bx bx-folder-open"></i>
                    <p>No cases in this bookmark collection</p>
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table className="cases-table">
                      <thead>
                        <tr>
                          <th className="col-party">
                            <i className="bx bx-user-circle"></i>
                            Party Details
                          </th>
                          <th className="col-date">
                            <i className="bx bx-calendar"></i>
                            Date
                          </th>
                          <th className="col-action">
                            <i className="bx bx-cog"></i>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {judgements.map((judgement, index) => (
                          <tr
                            key={judgement.highlightLink}
                            className="case-row"
                          >
                            <td 
                              className="col-party"
                              onClick={() => handleJudgementClick(judgement)}
                            >
                              <div className="party-info">
                                <i className="bx bx-file-blank"></i>
                                <span>{judgement.appelantRespondant}</span>
                              </div>
                            </td>
                            <td 
                              className="col-date"
                              onClick={() => handleJudgementClick(judgement)}
                            >
                              <div className="date-info">
                                <i className="bx bx-calendar-event"></i>
                                <span>{formatDate(judgement.jDate)}</span>
                              </div>
                            </td>
                            <td className="col-action">
                              <button
                                className="delete-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm("Are you sure you want to delete this bookmark?")) {
                                    handleDelete(judgement.id);
                                  }
                                }}
                                title="Delete bookmark"
                              >
                                <i className="bx bx-trash"></i>
                                <span>Delete</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* ============================================
           BASE LAYOUT
        ============================================ */
        .bookmarks-layout {
          min-height: 100vh;
          background: #fafafa;
        }

        .bookmarks-main-content {
          padding-top: 140px;
          width: 100%;
        }

        /* ============================================
           HERO SECTION
        ============================================ */
        .bookmarks-hero {
          background: linear-gradient(135deg, #E91E63 0%, #E91E64 25%, #c41849 75%, #D81B60 100%);
          padding: 50px 20px 40px;
          text-align: center;
          color: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 50px;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .hero-badge i {
          font-size: 16px;
        }

        .hero-title {
          font-size: 2rem;
          font-weight: 800;
          margin: 0 0 10px 0;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1rem;
          opacity: 0.95;
          margin: 0;
          font-weight: 400;
        }

        /* ============================================
           BOOKMARKS CONTAINER
        ============================================ */
        .bookmarks-container {
          max-width: 1400px;
          margin: -30px auto 0;
          padding: 0 20px 40px;
          position: relative;
          z-index: 10;
        }

        .bookmarks-grid {
          display: grid;
          grid-template-columns: 320px 1fr;
          gap: 20px;
          min-height: 600px;
        }

        /* ============================================
           LEFT SIDEBAR
        ============================================ */
        .bookmarks-sidebar {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          height: fit-content;
          position: sticky;
          top: 160px;
        }

        .sidebar-header {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 14px;
          border-bottom: 2px solid #f5f5f5;
        }

        .header-icon {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 22px;
          flex-shrink: 0;
        }

        .header-content {
          flex: 1;
        }

        .sidebar-title {
          font-size: 16px;
          font-weight: 700;
          color: #333;
          margin: 0 0 3px 0;
        }

        .sidebar-subtitle {
          font-size: 12px;
          color: #666;
          margin: 0;
        }

        .sidebar-body {
          max-height: 500px;
          overflow-y: auto;
        }

        /* States */
        .loading-state,
        .error-state,
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          gap: 12px;
        }

        .loading-state i,
        .error-state i,
        .empty-state i {
          font-size: 40px;
          color: #E91E63;
        }

        .error-state i {
          color: #c41849;
        }

        .empty-state i {
          color: #ccc;
        }

        .loading-state p,
        .error-state p,
        .empty-state p {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

        /* Bookmarks List */
        .bookmarks-list {
          padding: 8px;
        }

        .bookmark-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          margin-bottom: 6px;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .bookmark-item:hover {
          background: #f8f9fa;
          transform: translateX(4px);
        }

        .bookmark-item.active {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          border-color: #c41849;
          box-shadow: 0 2px 10px rgba(233, 30, 99, 0.15);
        }

        .bookmark-icon {
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.15), rgba(233, 30, 99, 0.1));
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #E91E63;
          font-size: 18px;
          flex-shrink: 0;
        }

        .bookmark-item.active .bookmark-icon {
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
        }

        .bookmark-info {
          flex: 1;
        }

        .bookmark-name {
          font-size: 14px;
          font-weight: 600;
          color: #333;
        }

        .bookmark-item.active .bookmark-name {
          color: #c41849;
        }

        .bookmark-arrow {
          color: #ccc;
          font-size: 18px;
          transition: all 0.3s ease;
        }

        .bookmark-item:hover .bookmark-arrow,
        .bookmark-item.active .bookmark-arrow {
          color: #c41849;
          transform: translateX(3px);
        }

        /* Scrollbar */
        .sidebar-body::-webkit-scrollbar {
          width: 5px;
        }

        .sidebar-body::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 4px;
        }

        /* ============================================
           RIGHT CONTENT
        ============================================ */
        .cases-content {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          display: flex;
          flex-direction: column;
        }

        .content-header {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          padding: 20px 24px;
          border-bottom: 2px solid #f5f5f5;
        }

        .header-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .content-title {
          font-size: 18px;
          font-weight: 700;
          color: #333;
          margin: 0 0 3px 0;
        }

        .content-subtitle {
          font-size: 13px;
          color: #666;
          margin: 0;
        }

        .content-body {
          flex: 1;
          overflow: auto;
        }

        /* Table States */
        .loading-table,
        .empty-table {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 15px;
        }

        .loading-table i,
        .empty-table i {
          font-size: 60px;
          color: #E91E63;
        }

        .empty-table i {
          color: #ddd;
        }

        .loading-table p,
        .empty-table p {
          font-size: 16px;
          color: #666;
          margin: 0;
        }

        /* ============================================
           TABLE
        ============================================ */
        .table-wrapper {
          overflow-x: auto;
        }

        .cases-table {
          width: 100%;
          border-collapse: collapse;
        }

        .cases-table thead {
          background: #fafafa;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .cases-table th {
          padding: 14px 20px;
          text-align: left;
          font-size: 13px;
          font-weight: 700;
          color: #555;
          border-bottom: 2px solid #e0e0e0;
          white-space: nowrap;
        }

        .cases-table th i {
          margin-right: 6px;
          color: #E91E63;
          font-size: 16px;
          vertical-align: middle;
        }

        .col-party {
          width: 60%;
        }

        .col-date {
          width: 20%;
        }

        .col-action {
          width: 20%;
          text-align: center !important;
        }

        .case-row {
          transition: all 0.2s ease;
          border-bottom: 1px solid #f5f5f5;
        }

        .case-row:hover {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.03), rgba(233, 30, 99, 0.02));
        }

        .cases-table td {
          padding: 16px 20px;
          font-size: 14px;
          color: #333;
        }

        .col-party,
        .col-date {
          cursor: pointer;
        }

        .party-info,
        .date-info {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .party-info i,
        .date-info i {
          font-size: 18px;
          color: #E91E63;
          flex-shrink: 0;
        }

        .party-info span {
          font-weight: 500;
        }

        .date-info span {
          color: #666;
        }

        /* Delete Button */
        .delete-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .delete-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
        }

        .delete-btn i {
          font-size: 16px;
        }

        /* ============================================
           ANIMATIONS
        ============================================ */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .bx-spin {
          animation: spin 1s linear infinite;
        }

        /* ============================================
           RESPONSIVE DESIGN
        ============================================ */
        @media (max-width: 1024px) {
          .bookmarks-grid {
            grid-template-columns: 280px 1fr;
            gap: 16px;
          }

          .bookmarks-sidebar {
            top: 150px;
          }
        }

        @media (max-width: 768px) {
          .bookmarks-main-content {
            padding-top: 130px;
          }

          .bookmarks-hero {
            padding: 35px 15px 30px;
          }

          .hero-title {
            font-size: 1.6rem;
          }

          .hero-subtitle {
            font-size: 0.95rem;
          }

          .bookmarks-container {
            margin-top: -20px;
            padding: 0 15px 30px;
          }

          .bookmarks-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .bookmarks-sidebar {
            position: relative;
            top: 0;
          }

          .sidebar-body {
            max-height: 300px;
          }

          .content-header {
            padding: 16px 18px;
          }

          .header-info {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .cases-table th,
          .cases-table td {
            padding: 12px 14px;
          }

          .party-info span {
            font-size: 13px;
          }

          .delete-btn span {
            display: none;
          }

          .delete-btn {
            padding: 8px 12px;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 1.4rem;
          }

          .hero-badge {
            padding: 6px 14px;
            font-size: 12px;
          }

          .sidebar-header {
            padding: 16px;
          }

          .header-icon {
            width: 38px;
            height: 38px;
            font-size: 18px;
          }

          .sidebar-title {
            font-size: 14px;
          }

          .content-header {
            padding: 14px 16px;
          }

          .content-title {
            font-size: 16px;
          }

          .cases-table th,
          .cases-table td {
            padding: 10px 12px;
            font-size: 12px;
          }

          .party-info i,
          .date-info i {
            font-size: 16px;
          }
        }
             /* ============================================
           REMOVE TOP WHITE SPACE
        ============================================ */
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default SaveBookmarks;